import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, BoltIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../context/LanguageContext';
import { track, ANALYTICS_EVENTS } from '../lib/analytics';
import { Helmet } from 'react-helmet-async';

/**
 * Two offers, one price each.
 *
 * The previous version was three tiers in euros with a "Most popular" badge —
 * the standard anchoring layout. It was replaced because the whole pitch is now
 * that there is nothing to compare: a tier table invites the visitor to work out
 * which one they need, and every minute spent on that is a minute not spent
 * getting in touch.
 *
 * Price and currency come from the translation file, so Spanish shows ARS and
 * English shows USD. That is deliberate — the two target markets are Argentina
 * and the United States, and the figures are not conversions of each other.
 */
const SynapsisPricing = () => {
  const { t } = useLanguage();

  // Both plan buttons scroll to the same form, so without the plan name there
  // is no way to tell which offer is actually generating the enquiries.
  const scrollToContact = (plan) => {
    track(ANALYTICS_EVENTS.CTA_CLICKED, { location: 'pricing', plan });
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Fires once when the pricing block first comes into view — the step between
  // landing and clicking, and the one that shows where people drop off.
  const pricingRef = useRef(null);
  const viewedRef = useRef(false);
  useEffect(() => {
    const node = pricingRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !viewedRef.current) {
            viewedRef.current = true;
            track(ANALYTICS_EVENTS.PRICING_VIEWED);
          }
        });
      },
      { threshold: 0.3 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const currency = t('pricing.currency', '$');
  const currencyCode = t('pricing.currencyCode', 'USD');

  const plans = [
    {
      key: 'web',
      name: t('pricing.web.name'),
      price: t('pricing.web.price'),
      delivery: t('pricing.web.delivery'),
      description: t('pricing.web.description'),
      features: [1, 2, 3, 4, 5, 6, 7].map((n) => t(`pricing.web.feature${n}`)),
      cta: t('pricing.web.cta'),
      highlight: true,
    },
    {
      key: 'system',
      name: t('pricing.system.name'),
      price: t('pricing.system.price'),
      delivery: t('pricing.system.delivery'),
      description: t('pricing.system.description'),
      features: [1, 2, 3, 4, 5, 6, 7].map((n) => t(`pricing.system.feature${n}`)),
      cta: t('pricing.system.cta'),
      highlight: false,
    },
  ];

  // `price` is formatted for humans (25.000), which is not a valid schema.org
  // price — strip the separators rather than keeping a second copy of the
  // number that could drift out of sync with the one on screen.
  const numericPrice = (value) => String(value).replace(/[.,\s]/g, '');

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: plans.map((plan, idx) => ({
      '@type': 'Product',
      position: idx + 1,
      name: plan.name,
      description: plan.description,
      offers: {
        '@type': 'Offer',
        price: numericPrice(plan.price),
        priceCurrency: currencyCode,
        availability: 'https://schema.org/InStock',
      },
    })),
  };

  return (
    <section ref={pricingRef} className="py-16 md:py-24 px-4 relative overflow-hidden">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <div className="absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 grid-pattern opacity-10" />
      </div>

      <div className="max-w-5xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            {t('pricing.title')} <span className="text-accent-400">{t('pricing.titleHighlight')}</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto">
            {t('pricing.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`surface rounded-2xl overflow-hidden flex flex-col ${
                plan.highlight ? 'ring-1 ring-accent-500 border-accent-500/50' : ''
              }`}
            >
              <div className="p-6 md:p-8 flex flex-col h-full">
                <div className="text-center mb-6">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-2">{plan.name}</h3>
                  <p className="text-gray-300 text-sm mb-6 min-h-[2.5rem]">{plan.description}</p>

                  <div className="flex items-baseline justify-center gap-1.5 mb-3">
                    <span className="text-gray-400 text-2xl font-mono-num">{currency}</span>
                    <span className="font-mono-num text-4xl md:text-5xl font-bold text-ink">
                      {plan.price}
                    </span>
                    <span className="text-gray-500 text-sm font-mono-num self-end mb-1.5">
                      {currencyCode}
                    </span>
                  </div>

                  {/* The date is the other half of the offer, so it sits with the
                      price rather than further down the feature list. */}
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent-500/15 border border-accent-500/30">
                    <BoltIcon className="w-3.5 h-3.5 text-accent-400" aria-hidden="true" />
                    <span className="text-accent-400 text-sm font-semibold">{plan.delivery}</span>
                  </div>
                </div>

                <div className="space-y-3 mb-8 flex-grow">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="flex-shrink-0 p-1 bg-accent-500/15 border border-accent-500/30 rounded-full mt-0.5">
                        <CheckIcon className="w-3.5 h-3.5 text-accent-400" />
                      </div>
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className={`w-full py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors ${
                    plan.highlight
                      ? 'bg-accent-500 hover:bg-accent-600 text-white'
                      : 'surface hover:border-accent-500/40 text-ink'
                  }`}
                  onClick={() => scrollToContact(plan.key)}
                >
                  {plan.cta}
                  <ArrowRightIcon className="w-4 h-4" />
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* The single strongest line on the page: it removes the buyer's risk
            entirely, and it needs no asterisk to do it. */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
          className="mt-10 md:mt-12 surface rounded-2xl p-6 md:p-8 text-center"
        >
          <p className="text-xl md:text-2xl font-bold text-ink mb-2">{t('pricing.guarantee')}</p>
          <p className="text-gray-300">{t('pricing.guaranteeSub')}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-8 text-center"
        >
          <p className="text-gray-400">
            {t('pricing.customSolution')}{' '}
            <a href="#contact" className="font-medium text-accent-400 hover:text-accent-300 underline">
              {t('pricing.contactUs')}
            </a>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default SynapsisPricing;
